/*
  Athletes: carousel + discipline filter.
  Custom file (not part of Turbo). See CUSTOMIZATIONS.md.

  Both components are custom elements, so they set themselves up whenever their markup is inserted
  and clean up when removed. That covers first page load, Turbo's InstantClick page swaps, and the
  theme editor re-rendering a section (shopify:section:load / unload) without any extra wiring.
  This file may be included by several sections and re-run by InstantClick, hence the guards.
*/
(function () {
  if (!window.customElements) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- Carousel */

  class AthleteCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-carousel-slide]'));
      this.controls = this.querySelector('[data-carousel-controls]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.toggleButton = this.querySelector('[data-carousel-toggle]');
      this.toggleLabel = this.querySelector('[data-carousel-toggle-label]');
      if (!this.track || !this.slides.length) return;

      this.autoplayDelay = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
      this.autoplayEnabled = this.hasAttribute('data-autoplay') && !reducedMotion.matches;
      this.userPaused = false;
      this.hovering = false;
      this.focused = false;
      this.selectedInEditor = false;
      this.timer = null;
      this.frame = null;

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPrev = this.go.bind(this, -1);
      this.onNext = this.go.bind(this, 1);
      this.onToggle = this.onToggle.bind(this);
      this.onEnter = this.setFlag.bind(this, 'hovering', true);
      this.onLeave = this.setFlag.bind(this, 'hovering', false);
      this.onFocusIn = this.setFlag.bind(this, 'focused', true);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibility = this.syncAutoplay.bind(this);
      this.onEditorSelect = this.onEditorSelect.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      if (this.toggleButton) this.toggleButton.addEventListener('click', this.onToggle);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorSelect);
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', this.onMotionChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.track);
      }

      if (this.toggleButton && !this.autoplayEnabled) this.toggleButton.hidden = true;
      if (this.controls) this.controls.hidden = false;
      this.updateButtons();
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this.onEditorSelect);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', this.onMotionChange);
    }

    behavior() {
      return reducedMotion.matches ? 'auto' : 'smooth';
    }

    step() {
      var first = this.slides[0];
      var second = this.slides[1];
      if (second) return second.offsetLeft - first.offsetLeft;
      return first.getBoundingClientRect().width;
    }

    atStart() {
      return this.track.scrollLeft <= 1;
    }

    atEnd() {
      return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
    }

    go(direction) {
      this.track.scrollBy({ left: direction * this.step(), behavior: this.behavior() });
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(
        function () {
          this.frame = null;
          this.updateButtons();
        }.bind(this)
      );
    }

    updateButtons() {
      var scrollable = this.track.scrollWidth > this.track.clientWidth + 1;
      this.classList.toggle('is-scrollable', scrollable);
      if (this.prevButton) this.prevButton.disabled = !scrollable || this.atStart();
      if (this.nextButton) this.nextButton.disabled = !scrollable || this.atEnd();
    }

    onKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      }
    }

    /* Autoplay: runs only when enabled, not paused by the visitor, not hovered/focused,
       tab visible, and not selected in the theme editor. */
    setFlag(flag, value) {
      this[flag] = value;
      this.syncAutoplay();
    }

    onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.setFlag('focused', false);
    }

    onEditorSelect(event) {
      if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
      this.setFlag('selectedInEditor', event.type === 'shopify:section:select');
    }

    onMotionChange() {
      this.autoplayEnabled = this.hasAttribute('data-autoplay') && !reducedMotion.matches;
      if (this.toggleButton) this.toggleButton.hidden = !this.autoplayEnabled;
      this.syncAutoplay();
    }

    onToggle() {
      this.userPaused = !this.userPaused;
      this.toggleButton.setAttribute('aria-pressed', String(this.userPaused));
      this.toggleButton.classList.toggle('is-paused', this.userPaused);
      if (this.toggleLabel) this.toggleLabel.textContent = this.userPaused ? 'Play carousel' : 'Pause carousel';
      this.syncAutoplay();
    }

    syncAutoplay() {
      var shouldRun =
        this.autoplayEnabled &&
        !this.userPaused &&
        !this.hovering &&
        !this.focused &&
        !this.selectedInEditor &&
        !document.hidden &&
        this.track.scrollWidth > this.track.clientWidth + 1;

      if (shouldRun && !this.timer) {
        this.timer = setInterval(this.advance.bind(this), this.autoplayDelay);
      } else if (!shouldRun) {
        this.stopAutoplay();
      }
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      if (this.atEnd()) {
        this.track.scrollTo({ left: 0, behavior: this.behavior() });
      } else {
        this.go(1);
      }
    }
  }

  /* ------------------------------------------------------------------ Filter */

  class AthleteFilter extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-filter-bar]');
      this.pills = Array.prototype.slice.call(this.querySelectorAll('[data-filter-value]'));
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-discipline-item]'));
      this.status = this.querySelector('[data-filter-status]');
      this.empty = this.querySelector('[data-filter-empty]');
      if (!this.bar || !this.pills.length || !this.items.length) return;

      this.onClick = this.onClick.bind(this);
      this.bar.addEventListener('click', this.onClick);
      this.bar.hidden = false;

      var requested = (new URLSearchParams(window.location.search).get('discipline') || '').toLowerCase();
      this.apply(this.hasPill(requested) ? requested : 'all', false);
    }

    disconnectedCallback() {
      if (this.bar && this.onClick) this.bar.removeEventListener('click', this.onClick);
    }

    hasPill(value) {
      return this.pills.some(function (pill) {
        return pill.dataset.filterValue === value;
      });
    }

    onClick(event) {
      var pill = event.target.closest('[data-filter-value]');
      if (!pill || !this.bar.contains(pill)) return;
      this.apply(pill.dataset.filterValue, true);
    }

    apply(value, updateUrl) {
      var shown = 0;

      this.pills.forEach(function (pill) {
        pill.setAttribute('aria-pressed', String(pill.dataset.filterValue === value));
      });

      this.items.forEach(function (item) {
        var card = item.querySelector('[data-discipline]');
        var disciplines = card ? card.dataset.discipline.split(' ') : [];
        var match = value === 'all' || disciplines.indexOf(value) !== -1;
        item.hidden = !match;
        if (match) shown += 1;
      });

      if (this.empty) this.empty.hidden = shown > 0;
      if (this.status && updateUrl) {
        var template = shown === 1 ? this.dataset.countOne : this.dataset.countOther;
        this.status.textContent = (template || '').replace('[count]', shown);
      }

      if (updateUrl && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        if (value === 'all') {
          url.searchParams.delete('discipline');
        } else {
          url.searchParams.set('discipline', value);
        }
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }
  }

  /* ------------------------------------------------------------ Video file */

  /* Big play overlay for native <video> (YouTube/Vimeo bring their own). Without JS the browser's controls are
     shown as normal; with JS they're hidden until the first play, like YouTube's cover. */
  class AthleteVideoPlayer extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-video-play]');
      if (!this.video || !this.button) return;

      this.onPlayClick = this.onPlayClick.bind(this);
      this.onPlay = this.onPlay.bind(this);
      this.onEnded = this.onEnded.bind(this);

      this.button.addEventListener('click', this.onPlayClick);
      this.video.addEventListener('play', this.onPlay);
      this.video.addEventListener('ended', this.onEnded);

      if (this.video.paused && this.video.played.length === 0) {
        this.video.controls = false;
        this.button.hidden = false;
      }
    }

    disconnectedCallback() {
      if (!this.video || !this.button) return;
      this.button.removeEventListener('click', this.onPlayClick);
      this.video.removeEventListener('play', this.onPlay);
      this.video.removeEventListener('ended', this.onEnded);
    }

    onPlayClick() {
      this.video.controls = true;
      var playing = this.video.play();
      if (playing && playing.catch) {
        playing.catch(
          function () {
            this.button.hidden = true;
          }.bind(this)
        );
      }
      this.video.focus();
    }

    onPlay() {
      this.video.controls = true;
      this.button.hidden = true;
    }

    onEnded() {
      this.button.hidden = false;
    }
  }

  if (!customElements.get('athlete-carousel')) customElements.define('athlete-carousel', AthleteCarousel);
  if (!customElements.get('athlete-filter')) customElements.define('athlete-filter', AthleteFilter);
  if (!customElements.get('athlete-video-player')) customElements.define('athlete-video-player', AthleteVideoPlayer);
})();
